import { fetchWithAuth, BASE_URL } from './client';

/**
 * SMS API - Two-way messaging for Ultra and Enterprise subscribers
 * Cost: 10 credits per SMS segment (160 chars/segment)
 */

// ============ SEND SMS ============

/**
 * Send a direct SMS message
 * POST /api/sms/send
 */
export async function sendSMS({ toPhone, message, recipientName, journeyId }) {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            to_phone: toPhone,
            message,
            recipient_name: recipientName,
            journey_id: journeyId
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 403) {
            throw new Error(error?.detail?.message || 'SMS messaging requires Ultra or Enterprise plan');
        }
        if (response.status === 402) {
            throw new Error(error?.detail?.message || 'Insufficient credits to send SMS');
        }
        throw new Error(error?.detail || `Failed to send SMS: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Send SMS using a template
 * POST /api/sms/send-template
 */
export async function sendSMSTemplate({ toPhone, templateId, variables, recipientName, journeyId }) {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/send-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            to_phone: toPhone,
            template_id: templateId,
            variables,
            recipient_name: recipientName,
            journey_id: journeyId
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 400 && error?.detail?.includes('Missing template variables')) {
            throw new Error(error.detail);
        }
        throw new Error(error?.detail?.message || error?.detail || `Failed to send SMS: ${response.statusText}`);
    }

    return response.json();
}

// ============ MESSAGE HISTORY ============

/**
 * Get sent message history
 * GET /api/sms/history
 */
export async function getSMSHistory({ limit = 50, journeyId } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (journeyId) params.append('journey_id', journeyId);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/sms/history${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, { cache: 'no-store' });

    if (!response.ok) {
        throw new Error(`Failed to fetch SMS history: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get conversation thread with a phone number
 * GET /api/sms/conversation/{phone}
 */
export async function getConversation(phone, { limit = 50 } = {}) {
    const cleanPhone = phone.replace(/\D/g, '');
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/api/sms/conversation/${encodeURIComponent(cleanPhone)}${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, { cache: 'no-store' });

    if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.statusText}`);
    }

    return response.json();
}

// ============ INBOX ============

/**
 * Get inbox (incoming messages)
 * GET /api/sms/inbox
 */
export async function getInbox({ limit = 50, unreadOnly = false } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (unreadOnly) params.append('unread_only', 'true');

    const queryString = params.toString();
    const url = `${BASE_URL}/api/sms/inbox${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, { cache: 'no-store' });

    if (!response.ok) {
        throw new Error(`Failed to fetch inbox: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get unread message count
 * GET /api/sms/inbox/unread-count
 */
export async function getUnreadCount() {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/inbox/unread-count`, {
        cache: 'no-store'
    });

    if (!response.ok) {
        // Return 0 on error to avoid breaking the UI
        return { unread_count: 0 };
    }

    return response.json();
}

/**
 * Mark messages as read
 * POST /api/sms/inbox/mark-read
 */
export async function markAsRead(messageIds) {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/inbox/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_ids: messageIds })
    });

    if (!response.ok) {
        throw new Error(`Failed to mark messages as read: ${response.statusText}`);
    }

    return response.json();
}

// ============ TEMPLATES ============

/**
 * Create a new template
 * POST /api/sms/templates
 */
export async function createTemplate({ name, body, description, triggerType }) {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            name,
            body,
            description,
            trigger_type: triggerType
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.detail || `Failed to create template: ${response.statusText}`);
    }

    return response.json();
}

/**
 * List all templates
 * GET /api/sms/templates
 */
export async function getTemplates() {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/templates`, {
        cache: 'no-store'
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get a specific template
 * GET /api/sms/templates/{template_id}
 */
export async function getTemplate(templateId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/templates/${encodeURIComponent(templateId)}`, {
        cache: 'no-store'
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Update a template
 * PATCH /api/sms/templates/{template_id}
 */
export async function updateTemplate(templateId, { name, body, description, triggerType, isActive }) {
    const payload = {};
    if (name !== undefined) payload.name = name;
    if (body !== undefined) payload.body = body;
    if (description !== undefined) payload.description = description;
    if (triggerType !== undefined) payload.trigger_type = triggerType;
    if (isActive !== undefined) payload.is_active = isActive;

    const response = await fetchWithAuth(`${BASE_URL}/api/sms/templates/${encodeURIComponent(templateId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.detail || `Failed to update template: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Delete a template
 * DELETE /api/sms/templates/{template_id}
 */
export async function deleteTemplate(templateId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/templates/${encodeURIComponent(templateId)}`, {
        method: 'DELETE'
    });

    if (!response.ok) {
        throw new Error(`Failed to delete template: ${response.statusText}`);
    }

    return response.json();
}

// ============ STATS & OPT-OUT ============

/**
 * Get SMS statistics
 * GET /api/sms/stats
 */
export async function getStats() {
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/stats`, {
        cache: 'no-store'
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch stats: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Check opt-out status for a phone number
 * GET /api/sms/optouts/{phone}
 */
export async function checkOptOut(phone) {
    const cleanPhone = phone.replace(/\D/g, '');
    const response = await fetchWithAuth(`${BASE_URL}/api/sms/optouts/${encodeURIComponent(cleanPhone)}`, {
        cache: 'no-store'
    });

    if (!response.ok) {
        throw new Error(`Failed to check opt-out status: ${response.statusText}`);
    }

    return response.json();
}

// ============ UTILITIES ============

/**
 * Calculate number of SMS segments for a message
 * Standard SMS: 160 chars per segment
 * Unicode SMS: 70 chars per segment
 */
export function calculateSegments(message) {
    if (!message) return { segments: 0, chars: 0, isUnicode: false };

    // Check if message contains non-GSM characters (requires Unicode)
    const gsmRegex = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑܧ¿a-zäöñüà]*$/;
    const isUnicode = !gsmRegex.test(message);

    const charsPerSegment = isUnicode ? 70 : 160;
    const chars = message.length;
    const segments = Math.ceil(chars / charsPerSegment);

    return { segments, chars, isUnicode, charsPerSegment };
}

/**
 * Calculate credits cost for a message
 * 10 credits per segment
 */
export function calculateCredits(message) {
    const { segments } = calculateSegments(message);
    return segments * 10;
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone) {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');

    // Handle US numbers
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    return phone;
}
